import { Pagination } from "@mui/material";
import { useTimeout } from "@shared/frontend/hooks";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHyperTable } from "../../HyperTable";
import type { HyperTableComponents } from "../../types";

export interface HyperTablePaginateProps<Entity> {
  overrideComponent?: Required<HyperTableComponents<Entity>>["paginate"];
}

const HyperTablePaginate = <Entity extends any>(
  props: HyperTablePaginateProps<Entity>,
) => {
  const { overrideComponent } = props;
  const { pagination, findResult, setPagination, refetch } =
    useHyperTable<Entity>();
  const { pageIndex, pageSize } = pagination;
  const { totalRows } = findResult;

  const [shownPageIndex, setShownPageIndex] = useState<number>(pageIndex);

  useEffect(() => {
    if (shownPageIndex === pageIndex) return;
    timeout.set(() => {
      setPagination((prev) => ({
        ...prev,
        pageIndex: shownPageIndex,
      }));
    }, 200);
  }, [shownPageIndex]);

  useEffect(() => {
    setShownPageIndex(pagination.pageIndex);
  }, [pagination.pageIndex]);

  const timeout = useTimeout();

  const pageCount = useMemo<number>(() => {
    if (!totalRows) return 0;
    return Math.ceil(totalRows / pageSize);
  }, [pageSize, totalRows]);

  const nextPage = useCallback(() => {
    if (pageIndex >= pageCount - 1) return;
    setShownPageIndex((prev) => prev + 1);
  }, [pageIndex, pageCount]);

  const prevPage = useCallback(() => {
    if (pageIndex <= 0) return;
    setShownPageIndex((prev) => prev - 1);
  }, [pageIndex]);

  const goToPage = useCallback(
    (page: number) => {
      if (page < 0 || page >= pageCount) return;
      setShownPageIndex(page);
    },
    [pageCount],
  );

  const handlePageChange = useCallback(
    (e: React.ChangeEvent<unknown>, page: number) => {
      if (
        e.currentTarget instanceof HTMLElement &&
        e.currentTarget.classList.contains("MuiPaginationItem-previousNext")
      ) {
        // If the button pressed is the next or previous button
        goToPage(page - 1);
      } else {
        timeout.clear();
        if (page === shownPageIndex + 1) {
          refetch("remove");
        } else {
          setShownPageIndex(page - 1);
          setPagination((prev) => ({
            ...prev,
            pageIndex: page - 1,
          }));
        }
      }
    },
    [
      goToPage,
      timeout,
      shownPageIndex,
      refetch,
      setShownPageIndex,
      setPagination,
    ],
  );

  if (overrideComponent) {
    const C = overrideComponent;
    return (
      <C
        currentPage={pageIndex}
        totalPages={pageCount}
        onNextPage={nextPage}
        onPrevPage={prevPage}
        onPageChange={goToPage}
      />
    );
  }

  if (pageCount <= 1) return null;

  return (
    <Pagination
      sx={(theme) => ({
        ".MuiPagination-ul": {
          flexDirection: "row",
          ".MuiPaginationItem-previousNext": {
            transform: "rotate(180deg)",
          },
          ".Mui-selected": {
            border: `1px solid ${theme.palette.secondary.main}`,
            backgroundColor: `${theme.palette.secondary.main.slice(0, 7)}33`, // Adding 20% opacity
            "&:hover": {
              backgroundColor: `${theme.palette.secondary.main.slice(0, 7)}66`, // Adding 40% opacity
            },
          },
        },
      })}
      size="small"
      count={pageCount}
      siblingCount={1}
      page={shownPageIndex + 1}
      onChange={handlePageChange}
      color="secondary"
    />
  );
};

export default HyperTablePaginate;
